import React from 'react';
import { Box, Button, Checkbox, FormControlLabel, TextField } from '@mui/material';
import { IconArrowUp, IconArrowDown, IconTrash, IconPlus } from '@tabler/icons-react';
import type { AdditionalFieldOption } from '../../../types/ticketTypeTemplate';
import { CHECKBOX_SX, FIELD_SX } from '../../../styles/designSystem';
import '../EditorCard.css';

interface OptionsEditorProps {
  options: AdditionalFieldOption[];
  onChange: (options: AdditionalFieldOption[]) => void;
}

export const OptionsEditor: React.FC<OptionsEditorProps> = ({ options, onChange }) => {
  const setAt = (index: number, patch: Partial<AdditionalFieldOption>) => {
    const next = [...options];
    next[index] = { ...next[index], ...patch };
    onChange(next);
  };

  const removeAt = (index: number) => {
    onChange(options.filter((_, i) => i !== index));
  };

  const move = (index: number, direction: 'up' | 'down') => {
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= options.length) return;
    const next = [...options];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const add = () => {
    onChange([...options, { label: '', value: '' }]);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <span className="editor-section-title">Options</span>

      {options.length === 0 ? (
        <div className="editor-empty">No options. Add at least one for dropdown fields.</div>
      ) : (
        <div className="editor-rows">
          {options.map((opt, index) => (
            <div className="editor-row" key={index}>
              <TextField
                size="small"
                label="Label"
                value={opt.label}
                onChange={(e) => setAt(index, { label: e.target.value })}
                sx={[{ flex: 1 }, FIELD_SX]}
              />
              <TextField
                size="small"
                label="Value"
                value={opt.value}
                onChange={(e) => setAt(index, { value: e.target.value })}
                sx={[{ flex: 1 }, FIELD_SX]}
                slotProps={{ input: { sx: { fontFamily: 'monospace' } } }}
              />
              <FormControlLabel
                sx={{ m: 0, flexShrink: 0, '& .MuiFormControlLabel-label': { fontSize: 11, color: '#646464' } }}
                control={
                  <Checkbox
                    size="small"
                    checked={!!opt.recommended}
                    onChange={(e) => setAt(index, { recommended: e.target.checked || undefined })}
                    sx={CHECKBOX_SX}
                  />
                }
                label="Recommended"
              />
              <div className="editor-row__actions">
                {index > 0 && (
                  <button type="button" className="editor-icon-btn" title="Move up" onClick={() => move(index, 'up')}>
                    <IconArrowUp size={15} stroke={1.5} />
                  </button>
                )}
                {index < options.length - 1 && (
                  <button type="button" className="editor-icon-btn" title="Move down" onClick={() => move(index, 'down')}>
                    <IconArrowDown size={15} stroke={1.5} />
                  </button>
                )}
                <button
                  type="button"
                  className="editor-icon-btn editor-icon-btn--danger"
                  title="Remove"
                  onClick={() => removeAt(index)}
                >
                  <IconTrash size={15} stroke={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Button
        startIcon={<IconPlus size={16} stroke={1.5} />}
        size="small"
        onClick={add}
        sx={{ alignSelf: 'flex-start', color: '#4A90D9', '&:hover': { bgcolor: '#EDF5FF' } }}
      >
        Add Option
      </Button>
    </Box>
  );
};
